interface PendingRequest<T> {
  promise: Promise<T>;
  abortController: AbortController;
  timestamp: number;
}

class RequestDeduplication {
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private readonly REQUEST_TIMEOUT = 30000;
  private readonly CLEANUP_INTERVAL = 60000;

  constructor() {
    setInterval(() => this.cleanup(), this.CLEANUP_INTERVAL);
  }

  async execute<T>(
    key: string,
    requestFn: (signal: AbortSignal) => Promise<T>,
    options?: { timeout?: number }
  ): Promise<T> {
    const existing = this.pendingRequests.get(key);
    if (existing) {
      const age = Date.now() - existing.timestamp;
      if (age < 30000) {
        return existing.promise;
      }
      existing.abortController.abort();
      this.pendingRequests.delete(key);
    }

    const abortController = new AbortController();
    const timeout = options?.timeout || this.REQUEST_TIMEOUT;

    const promise = Promise.race([
      requestFn(abortController.signal),
      new Promise<T>((_, reject) =>
        setTimeout(() => {
          abortController.abort();
          reject(new Error("Request timeout"));
        }, timeout)
      ),
    ]).finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, {
      promise,
      abortController,
      timestamp: Date.now(),
    });

    return promise;
  }

  cancel(key: string): void {
    const request = this.pendingRequests.get(key);
    if (request) {
      request.abortController.abort();
      this.pendingRequests.delete(key);
    }
  }

  cancelAll(): void {
    this.pendingRequests.forEach((request) => {
      request.abortController.abort();
    });
    this.pendingRequests.clear();
  }

  private cleanup(): void {
    const now = Date.now();
    this.pendingRequests.forEach((request, key) => {
      if (now - request.timestamp > this.REQUEST_TIMEOUT) {
        request.abortController.abort();
        this.pendingRequests.delete(key);
      }
    });
  }

  hasPending(key: string): boolean {
    return this.pendingRequests.has(key);
  }
}

export const requestDeduplication = new RequestDeduplication();

export const generateRequestKey = (
  method: string,
  url: string,
  params?: Record<string, any>
): string => {
  const paramsString = params ? JSON.stringify(params) : "";
  return `${method}:${url}:${paramsString}`;
};
